<template>
    <div>
        <card-header :title="isNew ? 'New Announcement' : 'Announcement'" :icon="isNew ? 'fa-plus' : 'fa-bullhorn'"/>

        <card-body>
            <card-list>
                <subheader-list-item icon="fa-align-left" title="Message"/>
                <container-list-item padded>
                    <quill-editor v-model="post.body" :options="editorOptions" class="has-background-white content announcement-editor mb-4"/>

                    <!-- Both types push to the group when posted; the type sets the
                         notification title and how the app files it. -->
                    <b-field label="Type" label-position="inside" message="Posting sends a push notification to everyone on the trip.">
                        <b-select v-model="post.announcement_type" expanded>
                            <option value="general">General announcement</option>
                            <option value="schedule">Schedule update</option>
                        </b-select>
                    </b-field>

                    <!-- Poll options are fixed once created: votes reference
                         them, and retitling an option under voters would
                         falsify votes. The closing time is the one poll field
                         an edit may change (see the Poll card below). -->
                    <div v-if="isNew" class="mt-4">
                        <b-switch v-model="withPoll" size="is-small">Add a quick poll</b-switch>
                        <div v-if="withPoll" class="mt-3 poll-editor">
                            <b-field label="Answer type" label-position="inside" class="mb-3">
                                <b-select v-model="pollType" size="is-small" expanded>
                                    <option value="single">Pick one</option>
                                    <option value="multi">Pick several</option>
                                    <option value="text">Write an answer (free-form)</option>
                                </b-select>
                            </b-field>

                            <!-- A free-form poll has no options by definition. -->
                            <template v-if="pollType !== 'text'">
                                <b-field v-for="(option, i) in options" :key="i" class="mb-2">
                                    <b-input v-model="options[i]" :placeholder="'Option ' + (i + 1)" size="is-small" expanded/>
                                    <p class="control" v-if="options.length > 2">
                                        <b-button size="is-small" @click="options.splice(i, 1)"><i class="fas fa-times"></i></b-button>
                                    </p>
                                </b-field>
                                <div>
                                    <b-button size="is-small" type="is-text" @click="options.push('')" v-if="options.length < 10">
                                        <i class="fas fa-plus mr-2"></i>Add option
                                    </b-button>
                                </div>

                                <b-field label="How many may they pick?" label-position="inside" class="mt-2 mb-2" v-if="pollType === 'multi'">
                                    <b-select v-model="maxChoices" size="is-small" expanded>
                                        <option :value="0">Any number</option>
                                        <option v-for="n in maxChoiceRange" :key="n" :value="n">Up to {{ n }}</option>
                                    </b-select>
                                </b-field>

                                <b-switch v-model="allowOther" size="is-small" class="mt-2">
                                    Add an "Other" option they can type into
                                </b-switch>
                            </template>

                            <div class="columns mt-2 mb-0">
                                <div class="column is-5">
                                    <b-field label="Closes On" label-position="inside">
                                        <b-input type="date" size="is-small" v-model="closesDate"/>
                                    </b-field>
                                </div>
                                <div class="column is-4">
                                    <b-field label="Closes At" label-position="inside">
                                        <b-input type="time" size="is-small" v-model="closesTime"/>
                                    </b-field>
                                </div>
                                <div class="column">
                                    <p class="has-text-grey is-size-7 mt-2">Leave blank to stay open.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </container-list-item>
            </card-list>

            <card-list v-if="!isNew && post.poll_type">
                <subheader-list-item icon="fa-poll-people" title="Poll" :badge="post.voter_count"/>
                <container-list-item>
                    <data-row mini title="Answer type">{{ pollTypeLabel }}</data-row>
                    <data-row mini title="Responses">
                        {{ post.voter_count }} {{ post.voter_count === 1 ? 'person' : 'people' }}
                    </data-row>
                    <data-row v-for="option in post.options" :key="option.id" :title="option.label">
                        {{ option.votes }} vote{{ option.votes === 1 ? '' : 's' }}
                    </data-row>
                </container-list-item>

                <!-- The one poll field an edit may change: it says when
                     answering stops, not what was asked, so moving it can't
                     falsify a vote already cast. -->
                <container-list-item padded>
                    <div class="columns mb-0">
                        <div class="column is-5">
                            <b-field label="Closes On" label-position="inside">
                                <b-input type="date" size="is-small" v-model="closesDate"/>
                            </b-field>
                        </div>
                        <div class="column is-4">
                            <b-field label="Closes At" label-position="inside">
                                <b-input type="time" size="is-small" v-model="closesTime"/>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-button size="is-small" class="mt-1" v-if="closesDate" @click="closesDate = ''; closesTime = ''">
                                <i class="fas fa-times mr-2"></i>Reopen
                            </b-button>
                        </div>
                    </div>
                    <p class="has-text-grey is-size-7 mb-0" v-if="pollClosed">
                        <i class="fas fa-lock mr-1"></i>This poll is closed — no further answers are accepted.
                    </p>
                    <p class="has-text-grey is-size-7 mb-0" v-else-if="!closesDate">
                        Open indefinitely. Set a date and time to stop accepting answers.
                    </p>
                </container-list-item>

                <container-list-item padded no-border>
                    <b-button size="is-small" @click="$openCard('announcement-results', {tripId: props.tripId, postId: props.postId}, card)">
                        <i class="fas fa-chart-bar mr-2"></i>View Responses
                    </b-button>
                </container-list-item>
            </card-list>

            <card-list v-if="!isNew">
                <subheader-list-item icon="fa-comments" title="Replies" :badge="comments.length || null"/>
                <container-list-item padded v-if="!comments.length">
                    <p class="has-text-grey is-italic mb-0">No replies yet.</p>
                </container-list-item>
                <container-list-item padded v-for="comment in comments" :key="comment.id">
                    <div class="columns is-gapless is-mobile mb-1">
                        <div class="column">
                            <strong>{{ comment.author_name }}</strong>
                            <b-tag type="is-primary" size="is-small" class="ml-2" v-if="comment.is_staff_post">Staff</b-tag>
                            <b-tag type="is-warning" size="is-small" class="ml-2" v-if="comment.visibility === 'staff'"><i class="fas fa-lock mr-1"></i>Staff only</b-tag>
                            <b-tag size="is-small" class="ml-2" v-if="comment.hidden"><i class="fas fa-eye-slash mr-1"></i>Hidden</b-tag>
                        </div>
                        <div class="column is-narrow">
                            <span class="has-text-grey is-size-7 mr-3">{{ comment.created_at | moment('MMM Do, h:mma') }}</span>
                            <a class="mr-3" @click="hideComment(comment)">{{ comment.hidden ? 'Unhide' : 'Hide' }}</a>
                            <a class="has-text-danger" @click="deleteComment(comment)">Delete</a>
                        </div>
                    </div>
                    <p class="mb-0 comment-body">{{ comment.body }}</p>
                </container-list-item>
                <container-list-item padded no-border>
                    <b-field class="mb-0">
                        <b-input v-model="newReply" size="is-small" placeholder="Reply as staff..." expanded @keyup.enter.native="sendReply"/>
                        <p class="control">
                            <b-button size="is-small" type="is-primary" :disabled="!newReply.trim()" @click="sendReply">Send</b-button>
                        </p>
                    </b-field>
                </container-list-item>
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column" v-if="!isNew">
                    <b-button type="is-warning is-outlined" size="is-small" class="mr-2" @click="toggleHide">
                        <i class="far mr-1" :class="post.hidden ? 'fa-eye' : 'fa-eye-slash'"></i>{{ post.hidden ? 'Unhide' : 'Hide' }}
                    </b-button>
                    <b-button type="is-danger is-outlined" size="is-small" @click="deletePost">
                        <i class="far fa-trash-alt"></i><span class="ml-2" v-if="confirmDelete">Delete?</span>
                    </b-button>
                </div>
                <div class="column has-text-right">
                    <b-button type="is-primary" size="is-small" @click="submit" :disabled="!valid">
                        <i class="fas fa-check mr-2"></i>{{ isNew ? 'Post Announcement' : 'Save Changes' }}
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import DataRow from "@/TIER/components/DataRow";

    import {client as http} from '@/http_client';

    export default {
        name: "EditAnnouncement",
        props: ['card', 'props'],
        components: {DataRow, ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        data: function() {
            return {
                post: {body: '', announcement_type: 'general'},
                comments: [],
                withPoll: false,
                pollType: 'single',
                options: ['', ''],
                maxChoices: 0,
                allowOther: false,
                closesDate: '',
                closesTime: '',
                newReply: '',
                confirmDelete: false,
                // Slimmed toolbar — announcement-appropriate, not the full
                // page-editor kit AppEditor uses.
                editorOptions: {
                    modules: {
                        toolbar: [
                            ['bold', 'italic', 'underline'],
                            [{'list': 'ordered'}, {'list': 'bullet'}],
                            ['link', 'clean']
                        ]
                    },
                    placeholder: 'What does the group need to know?'
                }
            };
        },
        computed: {
            isNew: function() {
                return !this.props.postId;
            },
            cleanOptions: function() {
                return this.options.map(o => o.trim()).filter(Boolean);
            },
            // "Up to N" only makes sense strictly between 1 and every option;
            // at the top end it is just "any number".
            maxChoiceRange: function() {
                const count = Math.max(this.cleanOptions.length, 2);
                return Array.from({length: count - 1}, (_, i) => i + 1);
            },
            pollTypeLabel: function() {
                return {
                    single: 'Pick one',
                    multi: this.post.poll_max_choices
                        ? `Pick up to ${this.post.poll_max_choices}`
                        : 'Pick several',
                    text: 'Write an answer (free-form)'
                }[this.post.poll_type] || '';
            },
            // The date/time pair is entered in the browser's timezone; the API
            // stores and returns UTC.
            closesAt: function() {
                if (!this.closesDate) return null;
                const local = new Date(`${this.closesDate}T${this.closesTime || '23:59'}`);
                return isNaN(local.getTime()) ? null : local.toISOString();
            },
            pollClosed: function() {
                return !!this.closesAt && new Date(this.closesAt).getTime() <= Date.now();
            },
            // A free-form poll needs no options; a choice poll needs two.
            pollReady: function() {
                if (!this.withPoll) return false;
                return this.pollType === 'text' || this.cleanOptions.length >= 2;
            },
            // Quill's empty document is still markup (<p><br></p>) — judge
            // emptiness on the stripped text, like the server does.
            strippedBody: function() {
                return (this.post.body || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
            },
            valid: function() {
                if (this.withPoll && !this.pollReady) return false;
                return !!this.strippedBody || this.pollReady;
            }
        },
        methods: {
            load: function() {
                if (this.isNew) return;
                this.$emit('loading', true);
                http.get('/api/experience/posts/' + this.props.postId, {force: true}).then(response => {
                    const post = response.data;
                    // A plain-text post (composed in Navigator) opening in
                    // Quill: escape it and preserve line breaks, else the
                    // editor would parse it as markup.
                    if (post.body_format === 'text' && post.body) {
                        const esc = post.body
                            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                        post.body = '<p>' + esc.replace(/\n/g, '<br/>') + '</p>';
                    }
                    this.post = post;
                    // Existing closing time back into the local date/time pair.
                    if (post.poll_closes_at) {
                        this.closesDate = this.$moment(post.poll_closes_at).format('YYYY-MM-DD');
                        this.closesTime = this.$moment(post.poll_closes_at).format('HH:mm');
                    } else {
                        this.closesDate = '';
                        this.closesTime = '';
                    }
                    this.loadComments();
                });
            },
            loadComments: function() {
                http.get('/api/experience/posts/' + this.props.postId + '/comments', {force: true}).then(response => {
                    this.comments = response.data.comments;
                    this.$emit('loading', false);
                });
            },
            submit: function() {
                this.$emit('loading', true);
                const request = this.isNew
                    ? http.post('/api/experience/trips/' + this.props.tripId + '/announcements', {
                        body: this.post.body,
                        announcement_type: this.post.announcement_type,
                        poll_type: this.withPoll ? this.pollType : null,
                        options: this.withPoll && this.pollType !== 'text' ? this.cleanOptions : [],
                        poll_max_choices: this.pollType === 'multi' ? this.maxChoices : 0,
                        poll_allow_other: this.withPoll && this.pollType !== 'text' && this.allowOther,
                        poll_closes_at: this.closesAt
                    }, {force: true})
                    : http.put('/api/experience/posts/' + this.props.postId, {
                        body: this.post.body,
                        announcement_type: this.post.announcement_type,
                        // Ignored server-side unless this post is a poll.
                        poll_closes_at: this.closesAt
                    }, {force: true});

                request.then(() => {
                    this.$reloadCard('trip-announcements', {tripId: this.props.tripId});
                    this.$closeCard(this.card);
                });
            },
            toggleHide: function() {
                http.put('/api/experience/posts/' + this.props.postId + '/hide', {hidden: this.post.hidden ? 0 : 1}, {force: true}).then(() => {
                    this.post.hidden = this.post.hidden ? 0 : 1;
                    this.$reloadCard('trip-announcements', {tripId: this.props.tripId});
                });
            },
            deletePost: function() {
                if (this.confirmDelete) {
                    http.delete('/api/experience/posts/' + this.props.postId, {force: true}).then(() => {
                        this.$reloadCard('trip-announcements', {tripId: this.props.tripId});
                        this.$closeCard(this.card);
                    });
                } else {
                    this.confirmDelete = true;
                    setTimeout(() => {
                        this.confirmDelete = false;
                    }, 3000);
                }
            },
            sendReply: function() {
                if (!this.newReply.trim()) return;
                http.post('/api/experience/posts/' + this.props.postId + '/comments', {body: this.newReply}, {force: true}).then(() => {
                    this.newReply = '';
                    this.loadComments();
                });
            },
            hideComment: function(comment) {
                http.put('/api/experience/comments/' + comment.id + '/hide', {hidden: comment.hidden ? 0 : 1}, {force: true}).then(() => {
                    this.loadComments();
                });
            },
            deleteComment: function(comment) {
                http.delete('/api/experience/comments/' + comment.id, {force: true}).then(() => {
                    this.loadComments();
                });
            }
        },
        watch: {
            'props.postId': function() {
                this.load();
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .poll-editor {
        border-left: 3px solid #e7e7e7;
        padding-left: 0.8rem;
    }

    .announcement-editor ::v-deep .ql-editor {
        min-height: 120px;
    }

    .comment-body {
        white-space: pre-wrap;
        word-break: break-word;
    }
</style>
